/**
 * Library version manager — runtime hot-switching of @ergeon/draft-editor
 * and @ergeon/3d-lib.
 *
 * Overrides are requested via the Dashboard selector (persisted in
 * localStorage) or a shareable URL param:
 *   ?lib_version=draft-editor:15.47.0,3d-lib:42.6.7
 *
 * Bundles are pre-built by cad-lib-bundler and served same-origin from
 * `${LIB_VERSIONS_BASE_URL}/<lib>/<version>/<env>/bundle.js` (per-env because
 * worker chunks bake env-specific values), with availability listed in
 * `${LIB_VERSIONS_BASE_URL}/<lib>/manifest.json`.
 *
 * Truthfulness rules (the previous attempt silently fell back to the
 * built-in build while displaying the override version — never again):
 * - A version is only reported as running after the loaded bundle registered
 *   itself on window and its own getVersion() output is captured.
 * - Any failure keeps the built-in code active with state 'failed' and a
 *   human-readable reason; the UI shows that loudly instead of pretending.
 */

import {GLOBALS_SCHEMA, exposeLibraryGlobals} from './libraryGlobals';
import {
  OVERRIDES_CHANGED_EVENT,
  type LibName,
  type OverrideRequests,
  getOverrideRequests,
  resetRequestCacheForTests,
} from './libVersionOverrides';
import {getAppEnv} from './utils';

export * from './libVersionOverrides';

const LIB_VERSIONS_BASE_URL = process.env.LIB_VERSIONS_BASE_URL || '/lib-versions';

/**
 * The env segment of the bundle path: `<lib>/<version>/<env>/bundle.js`.
 * Bundles are built once per environment (worker chunks bake env-specific
 * literals — they can't read the host's runtime env), so the app requests the
 * bundle matching its own backend.
 *
 * Only staging and production are published to S3. Local dev defaults to the
 * `staging` bundle (its apidev backend is what the default proxy target,
 * caddev, serves) so a fresh clone's selector loads without any extra setup.
 * Setting LIB_VERSIONS_BUNDLE_ENV=development opts into locally-built dev
 * bundles (make lib-bundle LEVEL=development + LIB_VERSIONS_PROXY_TARGET at the
 * local static server) — for testing an override against a *local* backend.
 */
const resolveBundleEnv = (): string => {
  const explicit = process.env.LIB_VERSIONS_BUNDLE_ENV;
  if (explicit) return explicit;
  const appEnv = getAppEnv();
  return appEnv === 'production' ? 'production' : 'staging';
};
const BUNDLE_ENV = resolveBundleEnv();
const MANIFEST_TIMEOUT_MS = 10000;
const SCRIPT_TIMEOUT_MS = 30000;

interface ManifestVersionEntry {
  js: string;
  css: string | null;
  builtAt?: string;
  globalsSchema: number;
  /**
   * Subresource-Integrity hash of bundle.js per env (`{staging, production}`),
   * `sha256-<base64>`. Keyed by env because each env's worker chunks bake
   * different values, so bundle.js — and its hash — differ per env. Absent for
   * a locally-built development bundle, which is served without SRI.
   */
  integrity?: Record<string, string>;
}

export interface LibManifest {
  library: string;
  schema: number;
  versions: Record<string, ManifestVersionEntry>;
}

type OverrideState = 'loading' | 'active' | 'failed';

export interface OverrideStatus {
  lib: LibName;
  requested: string;
  /** Version reported by the loaded bundle's own getVersion(). */
  loadedVersion: string | null;
  state: OverrideState;
  reason?: string;
  /** True for the draft-editor entry auto-added when only 3d-lib is overridden. */
  implicit?: boolean;
}

const statuses = new Map<LibName, OverrideStatus>();

export const getOverrideStatus = (lib: LibName): OverrideStatus | null => statuses.get(lib) ?? null;

export const getOverrideStatuses = (): OverrideStatus[] => [...statuses.values()];

const emitChanged = (): void => {
  window.dispatchEvent(new CustomEvent(OVERRIDES_CHANGED_EVENT));
};

const manifestCache = new Map<LibName, Promise<LibManifest | null>>();

/**
 * Fetch a library's manifest. Returns null when unavailable. CloudFront
 * rewrites missing keys to index.html with HTTP 200, so "is it really the
 * manifest" is decided by JSON parsing + a library field check, not status.
 * Only successful fetches are cached — a transient failure must not pin
 * "version list unavailable" for the rest of the page's life.
 */
export const getManifest = (lib: LibName): Promise<LibManifest | null> => {
  let cached = manifestCache.get(lib);
  if (!cached) {
    cached = fetchManifest(lib).then((manifest) => {
      if (!manifest) manifestCache.delete(lib);
      return manifest;
    });
    manifestCache.set(lib, cached);
  }
  return cached;
};

// A manifest entry is only usable if it names its bundle and declares a numeric
// globalsSchema. Without the schema check, a malformed entry passes
// `undefined > GLOBALS_SCHEMA` (false) and gets offered as a working version,
// then fails at load — the untrusted manifest is validated here instead.
const isValidEntry = (entry: unknown): entry is ManifestVersionEntry => {
  if (typeof entry !== 'object' || entry === null) return false;
  const e = entry as Record<string, unknown>;
  return typeof e.js === 'string' && typeof e.globalsSchema === 'number';
};

// Drop malformed version entries rather than reject the whole manifest — one
// bad entry shouldn't hide every other valid version.
const sanitizeManifest = (data: LibManifest): LibManifest => ({
  ...data,
  versions: Object.fromEntries(Object.entries(data.versions).filter(([, entry]) => isValidEntry(entry))),
});

const isValidManifest = (data: LibManifest, lib: LibName): boolean =>
  data.library === lib && typeof data.versions === 'object' && data.versions !== null;

const fetchManifest = async (lib: LibName): Promise<LibManifest | null> => {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), MANIFEST_TIMEOUT_MS);
    const response = await fetch(`${LIB_VERSIONS_BASE_URL}/${lib}/manifest.json`, {
      cache: 'no-cache',
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!response.ok) return null;
    const data = (await response.json()) as LibManifest;
    return isValidManifest(data, lib) ? sanitizeManifest(data) : null;
  } catch {
    return null;
  }
};

// Stylesheets injected per library, so a later failure can clean them up.
const injectedCss = new Map<LibName, HTMLLinkElement>();

/**
 * Verify the stylesheet actually exists (CloudFront's SPA fallback serves
 * HTML with 200 for missing keys) before injecting it. The <link> itself is
 * fire-and-forget afterwards — the fetch already proved availability and
 * primes the browser cache.
 */
const isHtmlResponse = (response: Response): boolean =>
  (response.headers?.get?.('content-type') ?? '').includes('text/html');

const injectVerifiedCss = async (lib: LibName, href: string): Promise<void> => {
  // Timeout like fetchManifest/injectScript: without it a stalled socket here
  // never settles, loadOne never returns, and the route shows a permanent
  // Loader with no error — the silent-failure mode this module exists to catch.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SCRIPT_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(href, {cache: 'no-cache', signal: controller.signal});
  } finally {
    clearTimeout(timer);
  }
  if (!response.ok || isHtmlResponse(response)) {
    throw new Error(`stylesheet unavailable at ${href}`);
  }
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
  injectedCss.set(lib, link);
};

const injectScript = (src: string, integrity?: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const timer = setTimeout(() => {
      script.remove();
      reject(new Error(`Timed out loading ${src}`));
    }, SCRIPT_TIMEOUT_MS);
    // SRI on the entry bundle.js: the browser refuses to run it if its bytes
    // don't match the manifest hash, so tampering or a truncated upload fails
    // the load (caught below) rather than executing altered code. Scope is
    // entry-only — async chunk.<hash>.js and bundle.css are not integrity-
    // checked. No crossOrigin: the bundles are same-origin (the app's own
    // /lib-versions/), and setting crossorigin would force a CORS-mode fetch
    // the same-origin bucket doesn't answer, blocking the script before SRI runs.
    if (integrity) script.integrity = integrity;
    script.src = src;
    script.onload = () => {
      clearTimeout(timer);
      resolve();
    };
    script.onerror = () => {
      clearTimeout(timer);
      reject(new Error(`Failed to load ${src}`));
    };
    document.head.appendChild(script);
  });

const fail = (status: OverrideStatus, reason: string): void => {
  delete window.__ergeonLibOverrides?.[status.lib];
  injectedCss.get(status.lib)?.remove();
  injectedCss.delete(status.lib);
  status.state = 'failed';
  status.reason = reason;
};

/** Resolve the manifest entry for the requested version, or fail the status. */
const resolveManifestEntry = async (status: OverrideStatus): Promise<ManifestVersionEntry | null> => {
  const manifest = await getManifest(status.lib);
  if (!manifest) {
    fail(status, 'version list (manifest.json) unavailable');
    return null;
  }
  const entry = manifest.versions[status.requested];
  if (!entry) {
    fail(status, `version ${status.requested} has no published bundle`);
    return null;
  }
  if (entry.globalsSchema > GLOBALS_SCHEMA) {
    const detail = `bundle requires globals schema ${entry.globalsSchema}, app supports ${GLOBALS_SCHEMA}`;
    fail(status, `${detail} — redeploy the app first`);
    return null;
  }
  return entry;
};

/**
 * The script tag loading is not proof the override runs: CloudFront's SPA
 * fallback serves HTML with 200, and a parse error also leaves no trace.
 * The registered window global — and the bundle's own getVersion() — are
 * the truth test.
 */
const errorText = (error: unknown): string => (error instanceof Error ? error.message : String(error));

const readRegisteredVersion = (status: OverrideStatus): {getVersion?: () => string} | undefined => {
  const registered = window.__ergeonLibOverrides?.[status.lib];
  if (!registered || registered.version !== status.requested) return undefined;
  return registered.modules['exports/utils'] as {getVersion?: () => string} | undefined;
};

const verifyRegistered = (status: OverrideStatus): void => {
  const utils = readRegisteredVersion(status);
  if (!utils) {
    fail(status, `bundle loaded but did not register ${status.lib}@${status.requested}`);
    return;
  }
  const reportedVersion = utils.getVersion?.();
  if (!reportedVersion) {
    fail(status, 'loaded bundle does not expose getVersion() — cannot verify what is running');
    return;
  }
  status.loadedVersion = reportedVersion;
  status.state = 'active';
};

const loadAssets = async (lib: LibName, entry: ManifestVersionEntry, base: string): Promise<void> => {
  if (entry.css) await injectVerifiedCss(lib, `${base}/${entry.css}`);
  // Per-env hash; undefined for a local development bundle (not in the S3
  // manifest), which then loads without SRI — it's locally built, not fetched.
  await injectScript(`${base}/${entry.js}`, entry.integrity?.[BUNDLE_ENV]);
};

const loadOne = async (status: OverrideStatus): Promise<void> => {
  if (status.state !== 'loading') return;
  const entry = await resolveManifestEntry(status);
  if (!entry) return;

  const base = `${LIB_VERSIONS_BASE_URL}/${status.lib}/${status.requested}/${BUNDLE_ENV}`;
  try {
    await loadAssets(status.lib, entry, base);
  } catch (error) {
    fail(status, errorText(error));
    return;
  }
  verifyRegistered(status);
};

/**
 * Build the load plan, 3d-lib first: a draft-editor bundle must bind to the
 * overridden 3d-lib, so load order matters. When only 3d-lib is overridden,
 * draft-editor is implicitly loaded as a bundle at its built-in version —
 * the host-compiled draft-editor has static bindings to the built-in 3d-lib
 * and cannot see the override otherwise.
 */
const buildLoadPlan = async (requests: OverrideRequests): Promise<OverrideStatus[]> => {
  const plan: OverrideStatus[] = [];
  if (requests['3d-lib']) {
    plan.push({lib: '3d-lib', requested: requests['3d-lib'], loadedVersion: null, state: 'loading'});
  }
  if (requests['draft-editor']) {
    plan.push({lib: 'draft-editor', requested: requests['draft-editor'], loadedVersion: null, state: 'loading'});
  } else if (requests['3d-lib']) {
    const {getVersion} = await import('@ergeon/draft-editor/exports/utils');
    plan.push({lib: 'draft-editor', requested: getVersion(), loadedVersion: null, state: 'loading', implicit: true});
  }
  return plan;
};

/** Keep Sentry's package version tags truthful under overrides. */
const refreshSentryTags = async (): Promise<void> => {
  try {
    const [{setTags}, {getPackageVersionTags}] = await Promise.all([
      import('@sentry/react'),
      import('./packageVersions'),
    ]);
    setTags(getPackageVersionTags());
  } catch {
    // Sentry tagging is best-effort
  }
};

const failAll = (plan: OverrideStatus[], reason: string): void => {
  for (const status of plan) {
    status.state = 'failed';
    status.reason = reason;
  }
};

const exposeGlobalsOrFail = async (plan: OverrideStatus[]): Promise<boolean> => {
  try {
    await exposeLibraryGlobals();
    return true;
  } catch (error) {
    failAll(plan, `failed to expose host modules: ${errorText(error)}`);
    emitChanged();
    return false;
  }
};

const installPlan = (plan: OverrideStatus[]): void => {
  for (const status of plan) statuses.set(status.lib, status);
  emitChanged();
};

/**
 * A status must ALWAYS be installed for every requested lib — an absent
 * status reads as "loading" to the wrappers, so a rejection here without a
 * fallback would mean a permanent Loader with no error (the silent-failure
 * mode this module exists to prevent).
 */
const fallbackPlan = (requests: OverrideRequests, reason: string): OverrideStatus[] =>
  Object.entries(requests).map(([lib, requested]) => ({
    lib: lib as LibName,
    requested: requested as string,
    loadedVersion: null,
    state: 'failed' as const,
    reason,
  }));

const preparePlan = async (requests: OverrideRequests): Promise<OverrideStatus[]> => {
  try {
    return await buildLoadPlan(requests);
  } catch (error) {
    return fallbackPlan(requests, `failed to prepare override plan: ${errorText(error)}`);
  }
};

/**
 * Draft-editor binds to 3d-lib through __ergeonLibResolve, so a draft-editor
 * bundle running against a *failed* 3d-lib override falls back to the built-in
 * 3d-lib — a pair no manifest promised and nothing tested. If 3d-lib was
 * requested and failed, fail the dependent draft-editor entry too rather than
 * load an unvetted combination.
 */
const failedThreeD = (plan: OverrideStatus[]): OverrideStatus | undefined =>
  plan.find((s) => s.lib === '3d-lib' && s.state === 'failed');

const loadStatus = async (plan: OverrideStatus[], status: OverrideStatus): Promise<void> => {
  const threeD = status.lib === 'draft-editor' ? failedThreeD(plan) : undefined;
  if (threeD) {
    fail(status, `not loaded — its 3d-lib override (${threeD.requested}) failed`);
  } else {
    await loadOne(status);
  }
  emitChanged();
};

const loadAll = async (): Promise<OverrideStatus[]> => {
  const requests = getOverrideRequests();
  if (Object.keys(requests).length === 0) return [];

  const plan = await preparePlan(requests);
  installPlan(plan);
  if (!(await exposeGlobalsOrFail(plan))) return plan;

  for (const status of plan) await loadStatus(plan, status);
  await refreshSentryTags();
  return plan;
};

let loadPromise: Promise<OverrideStatus[]> | null = null;

/** Idempotently load all requested overrides. */
export const ensureOverridesLoaded = (): Promise<OverrideStatus[]> => {
  if (!loadPromise) loadPromise = loadAll();
  return loadPromise;
};

export const resetForTests = (): void => {
  resetRequestCacheForTests();
  loadPromise = null;
  statuses.clear();
  manifestCache.clear();
  injectedCss.clear();
};
